/**
 * Resolve a Leanpub/Markua `{icon:NAME}` directive to a bundled FontAwesome
 * icon. Leanpub's icon names ARE FontAwesome names (dna, user, robot,
 * life-ring, …), and this app already ships @fortawesome/free-solid-svg-icons,
 * so no new icon font/asset is needed.
 *
 * `resolveCalloutIcon(name, type)`:
 *  - a known `{icon:NAME}` wins;
 *  - otherwise fall back to a sensible per-type default;
 *  - unknown name + unknown type → a neutral info glyph (never null).
 */
import {
  faDna,
  faUser,
  faRobot,
  faLifeRing,
  faCircleInfo,
  faTriangleExclamation,
  faLightbulb,
  faCircleExclamation,
  faBug,
  faRocket,
  faBook,
  faBookOpen,
  faCode,
  faStar,
  faHeart,
  faFlask,
  faGear,
  faQuoteLeft,
  faCircleQuestion,
  faKey,
  faFire,
  faComment,
  faListCheck
} from '@fortawesome/free-solid-svg-icons';

// Leanpub/FontAwesome icon name → icon. Keys are normalised (lowercase, the
// leading `fa-` and any `-o`/solid/regular suffixes dropped). Aliases map v4/v5
// spellings onto the v6 glyph we ship.
const ICON_BY_NAME = {
  dna: faDna,
  user: faUser,
  users: faUser,
  robot: faRobot,
  'life-ring': faLifeRing,
  lifering: faLifeRing,
  'life-buoy': faLifeRing,
  info: faCircleInfo,
  'info-circle': faCircleInfo,
  'circle-info': faCircleInfo,
  warning: faTriangleExclamation,
  'exclamation-triangle': faTriangleExclamation,
  'triangle-exclamation': faTriangleExclamation,
  'exclamation-circle': faCircleExclamation,
  'circle-exclamation': faCircleExclamation,
  lightbulb: faLightbulb,
  bulb: faLightbulb,
  tip: faLightbulb,
  bug: faBug,
  rocket: faRocket,
  book: faBook,
  'book-open': faBookOpen,
  code: faCode,
  star: faStar,
  heart: faHeart,
  flask: faFlask,
  gear: faGear,
  cog: faGear,
  'quote-left': faQuoteLeft,
  quote: faQuoteLeft,
  question: faCircleQuestion,
  'question-circle': faCircleQuestion,
  'circle-question': faCircleQuestion,
  key: faKey,
  fire: faFire,
  comment: faComment,
  discussion: faComment,
  tasks: faListCheck,
  'list-check': faListCheck,
  checklist: faListCheck
};

// Per-type default when there is no `{icon:...}` (or it is unknown).
const DEFAULT_ICON_BY_TYPE = {
  aside: faCircleInfo,
  blurb: faQuoteLeft,
  note: faCircleInfo,
  warning: faTriangleExclamation,
  tip: faLightbulb
};

/** Normalise an incoming icon name to a map key. */
function normalizeName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/^fa[srlbdk]?-/, '') // drop a leading fa-/fas-/far-… prefix
    .replace(/-o$/, ''); // v4 "-o" outline suffix → solid equivalent
}

export function resolveCalloutIcon(name, type) {
  const key = normalizeName(name);
  if (key && ICON_BY_NAME[key]) return ICON_BY_NAME[key];
  return DEFAULT_ICON_BY_TYPE[type] || faCircleInfo;
}
